import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { dtcListState, vehicleState, toolState } from './states';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';


import { DtcList } from './DtcList';
import { StyledButton } from './components/shared/StyledButton';
import plusIcon from './assets/plus-icon.png';
import './VehicleSelection.css';
import { getYears, getMakes, getModels } from './services/VehicleService';
import { LocalStorageService } from './services/LocalStorageService';
import { Tool } from './types';

interface Year {
    yearId: number
    yearValue: number
}
interface Make {
    makeId: number
    makeName: string
}
interface Model {
    modelId: number
    modelName: string
}

const VehicleSelection = () => {
    const [vehicle, setVehicle] = useRecoilState(vehicleState);
    const [dtcs, setDtcs] = useRecoilState(dtcListState);
    const [selectedTool, setSelectedTool] = useRecoilState(toolState);

    const [years, setYears] = useState<Year[]>([]);
    const [getYearsLoading, setGetYearsLoading] = useState<boolean>(false);
    const [makes, setMakes] = useState<Make[]>([]);
    const [getMakesLoading, setGetmakesLoading] = useState<boolean>(false);
    const [models, setModels] = useState<Model[]>([]);
    const [getModelsLoading, setGetModelsLoading] = useState<boolean>(false);
    const [tools, setTools] = useState<Tool[]>([]);
    const [getToolsLoading, setGetToolsLoading] = useState<boolean>(false);


    const [selectMake, setSelectMake] = useState<{ id: number, label: string }>({ id: 0, label: 'Select Make' });
    const [selectModel, setSelectModel] = useState<{ id: number, label: string }>({ id: 0, label: 'Select Model' });
    const [selectYear, setSelectYear] = useState<{ id: number, label: string }>({ id: 0, label: 'Select Year' });

    useEffect(() => {
        setSelectMake({ id: 0, label: 'Select Make' });
        const makesKey = "makeApiData" + selectYear;
        const makesData = LocalStorageService.getItem<[]>(makesKey);
        if (makesData) {
            setMakes(makesData)
        } else {
            if (!getMakesLoading && selectYear && selectYear.id !== 0) {
                setGetmakesLoading(true);
                getMakes(selectYear.id)
                    .then(resp => {
                        setMakes(resp.data.makes)
                        LocalStorageService.setItem<[]>(makesKey, resp.data.makes);
                    })
                    .catch(error => console.error('Error fetching data:', error))
                    .finally(() => setGetmakesLoading(false));
            }
        }
        // eslint-disable-next-line
    }, [selectYear]);

    useEffect(() => {
        const yearsKey = "yearApiData";
        const yearsData = LocalStorageService.getItem<[]>(yearsKey);
        if (yearsData) {
            setYears(yearsData)
        }
        else {
            if (!getYearsLoading) {
                setGetYearsLoading(true);
                getYears()
                    .then(response => {
                        setYears(response.data.years)
                        LocalStorageService.setItem<[]>(yearsKey, response.data.years);
                    })
                    .catch(error => console.error('Error fetching data:', error))
                    .finally(() => setGetYearsLoading(false));
            }
        }
                
        const toolsData: Tool[] = [
            { toolName: '', toolPrettyLabel: 'Select Tool' },
            { toolName: '5610', toolPrettyLabel: 'Innova 5610' },
            { toolName: '5160rs', toolPrettyLabel: 'Innova 5160rs' },
            { toolName: 'sds43', toolPrettyLabel: 'Innova SDS43' }
        ];

        if (toolsData) {
            setTools(toolsData)
        }

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setSelectModel({ id: 0, label: 'Select Model' });
        if (!getModelsLoading
            && selectMake
            && selectMake.id !== 0
            && selectYear
            && selectYear.id !== 0) {
            setGetModelsLoading(true);

            getModels(selectYear.id, selectMake.id)
                .then(response => setModels(response.data.models))
                .catch(error => console.error('Error fetching data:', error))
                .finally(() => setGetModelsLoading(false));
        }
        // eslint-disable-next-line
    }, [selectMake]);

    const selectVehicle = (type: string, value: number, label: string) => {
        if (type === "year") {
            setSelectYear({ id: value, label: label });
            setSelectMake({ id: 0, label: 'Select Make' });
            setSelectModel({ id: 0, label: 'Select Model' });
            let newVehicle = { ...vehicle };
            newVehicle.make = undefined;
            newVehicle.model = undefined;
            newVehicle.year = label;
            setVehicle(newVehicle);
        } else if (type === "make") {
            setSelectMake({ id: value, label: label });
            setSelectModel({ id: 0, label: 'Select Model' });
            let newVehicle = { ...vehicle };
            newVehicle.model = undefined;
            newVehicle.make = label;
            setVehicle(newVehicle);
        } else if (type === "model") {
            setSelectModel({ id: value, label: label });
            let newVehicle = { ...vehicle };
            newVehicle.model = label;
            setVehicle(newVehicle);
        }
    }

    const selectTool = (type: string, value: string, label: string) => {
        if (type === "tool") {
            let newTool = { ...selectedTool };
            newTool.toolName = value;
            newTool.toolPrettyLabel = label;
            setSelectedTool(newTool);
        }
    }

    return (
        <Container fluid={true} className="form-container">
            <form>
                <Row className="select-row">
                    <Col sm={12} md={4} lg={4} className="form-group">
                        <label htmlFor="selectForYear" className='float-left'>Select Year<span className='required'>*</span></label>
                        <Autocomplete
                            id="selectForYear"
                            disableClearable
                            fullWidth={true}
                            options={years?.length ? years?.map(m => ({ id: m.yearId, label: String(m.yearValue) })) : []}
                            defaultValue={{ id: 0, label: 'Select Year' }}
                            value={selectYear}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    padding: '3px',
                                    fontSize: 18
                                }
                            }}
                            renderInput={(params) => <TextField sx={{ backgroundColor: 'white' }} {...params} />}
                            onChange={(event, newValue) => newValue && selectVehicle('year', newValue.id, newValue.label)}
                            loading={getYearsLoading}
                        />
                    </Col>
                    <Col sm={12} md={4} lg={4} className="form-group">
                        <label htmlFor="selectForMake" className='float-left'>Select Make<span className='required'>*</span></label>
                        <Autocomplete
                            id="selectForMake"
                            disableClearable
                            fullWidth={true}
                            options={makes?.length ? makes?.map(m => ({ id: m.makeId, label: String(m.makeName) })) : []}
                            defaultValue={{ id: 0, label: 'Select Make' }}
                            value={selectMake}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    padding: '3px',
                                    fontSize: 18
                                }
                            }}
                            renderInput={(params) => <TextField sx={{ backgroundColor: 'white' }} {...params} />}
                            onChange={(event, newValue) => newValue && selectVehicle('make', newValue.id, newValue.label)}
                            loading={getMakesLoading}
                        />
                    </Col>
                    <Col sm={12} md={4} lg={4} className="form-group">
                        <label htmlFor="selectForModel" className='float-left'>Select Model<span className='required'>*</span></label>
                        <Autocomplete
                            id="selectForModel"
                            disableClearable
                            fullWidth={true}
                            options={models?.length ? models?.map(m => ({ id: m.modelId, label: String(m.modelName) })) : []}
                            defaultValue={{ id: 0, label: 'Select Model' }}
                            value={selectModel}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    padding: '3px',
                                    fontSize: 18
                                }
                            }}
                            renderInput={(params) => <TextField sx={{ backgroundColor: 'white' }} {...params} />}
                            onChange={(event, newValue) => newValue && selectVehicle('model', newValue.id, newValue.label)}
                            loading={getModelsLoading}
                        />
                    </Col>
                </Row>
                <Row className="padding-row">
                    <Col sm={12} md={6} lg={5} className="form-group">
                        <div className='dtc-button'>
                            <label htmlFor="selectTools" className='float-left optional-section'>Tools<div className='optional-badge'>optional</div></label>
                            <Autocomplete
                                id="selectTools"
                                disableClearable
                                options={tools?.length ? tools?.map(t => ({ id: t.toolName, label: String(t.toolPrettyLabel) })) : []}
                                defaultValue={{ id: '', label: 'Select Tool' }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        padding: '3px',
                                        fontSize: 18
                                    }
                                }}
                                renderInput={(params) => <TextField sx={{ backgroundColor: 'white' }} {...params} />}
                                onChange={(event, newValue) => newValue && selectTool('tool', newValue.id, newValue.label)}
                                loading={getToolsLoading}
                            />
                        </div>
                    </Col>
                    <Col sm={12} md={6} lg={{span: 5, offset: 2}} className="form-group dtc-group">
                        <div className='dtc-button'>
                            <label htmlFor="addDtc" className='optional-section'>Diagnostic Trouble Code<div className='optional-badge'>optional</div></label>
                            <StyledButton
                                onClick={(e) => {
                                    e.preventDefault();
                                    setDtcs([...dtcs, {
                                        id: dtcs.length + 1,
                                        dtc: '',
                                        // severity: ''
                                    }]);
                                }}
                                buttonText={'Add DTC'}
                                image={<img src={plusIcon} alt="Plus Icon" style={{ width: '20px', height: '20px' }} />}
                                name="addDtc" />
                        </div>
                        <DtcList />
                    </Col>
                </Row>
            </form>
        </Container>
    );
};

export default VehicleSelection;
